import { useLayoutEffect, useRef } from 'react'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { api, apiBaseUrl } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { shouldTrackVisits } from '../lib/visitor-tracking'
import { createOpenTracker } from '../lib/page-perf/open-tracker'
import { createBatcher, createSender } from '../lib/page-perf/batcher'
import { normalizeApiPath } from '../lib/page-perf/api-path'
import { registerPagePerf } from '../lib/page-perf/control'

/*
 * PagePerfTracker — Page Performance, Phase 1 (Measure).
 *
 *   Mounted ONCE inside the router, next to <VisitorTracker />. Times every
 *   page open (the rules live in lib/page-perf/open-tracker.js) and posts
 *   the reports in the background to /page-performance/track. Renders
 *   nothing.
 *
 *   - Keyed by `location.pathname` only — search and hash are ignored, and
 *     nothing else about the page (title, form values, ids) is ever sent.
 *   - Watches the shared `api` axios instance through exactly ONE
 *     request/response interceptor pair. The pair is remembered on the
 *     instance itself, so a StrictMode double mount or an HMR re-run
 *     ejects the previous pair before installing a new one.
 *   - Measurement runs from the first frame (a full load starts at the
 *     browser's navigation start, before the session is known); the
 *     who-is-measured gate (`shouldTrackVisits`: signed-in organisation
 *     members, never the super admin, never while impersonating) is applied
 *     when a report is queued. When the gate closes, the open in progress is
 *     reported as abandoned and the queue is sent while the gate still held.
 *   - The auth store reaches the engine through lib/page-perf/control.js:
 *     flush before an account switch, discard on a stale tab.
 *   - Fire-and-forget: every browser API is guarded and every error is
 *     swallowed, so a measuring hiccup can never affect the app.
 */

const PAIR_KEY = Symbol.for('taskmagix.pagePerf.interceptors')
const FULL_KEY = Symbol.for('taskmagix.pagePerf.fullLoadClaimed')
// A string key (not a Symbol): axios copies own string keys when a config is
// re-sent, so the 401 → refresh → retry of one call keeps one id.
const ID_FIELD = '_pagePerfId'
// Never part of a page open: the trackers' own posts, and the shell's
// maintenance-status poll (it runs on a timer whatever page is open, so
// counting it would keep an open from settling and inflate its API figures).
const IGNORED_PATHS = ['/page-performance/track', '/visitor-logs/track', '/maintenance/status']
const MAX_RESOURCE_ENTRIES = 300
// Module-level so call ids stay unique for the life of the tab: a gate that
// remounts the tracker (PlanGate / MaintenanceGate) would otherwise restart
// at c1 and let the new engine end its own pending c1 when an old call replies.
let callSeq = 0

function uuid() {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const b = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b)
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

function supports(type) {
  try {
    const list = globalThis.PerformanceObserver?.supportedEntryTypes
    return Array.isArray(list) && list.includes(type)
  } catch {
    return false
  }
}

function contentLength(headers) {
  try {
    const raw =
      typeof headers?.get === 'function' ? headers.get('content-length') : headers?.['content-length']
    const n = Number(raw)
    return raw != null && raw !== '' && Number.isFinite(n) && n >= 0 ? n : undefined
  } catch {
    return undefined
  }
}

/** Build the measuring engine. Everything it touches is torn down by
 *  `dispose()`. */
function createEngine(isEnabled) {
  const perf = globalThis.performance
  const now = () => (perf && typeof perf.now === 'function' ? perf.now() : Date.now())
  const cleanups = []
  let started = false
  let ownsFullClaim = false
  // Set by flushNow / clearAll (the identity behind the tab is changing):
  // nothing is queued until the next route change.
  let suspended = false
  const mayQueue = () => !suspended && isEnabled()

  // Full-load browser figures, filled by the observers below.
  let navEntry = null
  let fcpMs
  const lcpTimes = []
  let clsMeasured = false
  let inpMeasured = false
  // Recent Resource Timing entries of API calls (observer-fed, so a full
  // global resource buffer does not hide them).
  const resources = new Map()
  let resourceCount = 0
  let apiOrigin = ''
  try {
    apiOrigin = new URL(apiBaseUrl, globalThis.location?.href).href
  } catch {
    apiOrigin = ''
  }

  /** The Resource Timing entry of one API call: the first entry for its
   *  URL that started at or after the call. */
  const findResource = (ref) => {
    if (!ref?.url) return null
    let list = resources.get(ref.url) || []
    if (!list.length) list = perf?.getEntriesByName?.(ref.url, 'resource') || []
    let best = null
    for (const e of list) {
      if (e.startTime + 1 < ref.at) continue
      if (!best || e.startTime < best.startTime) best = e
    }
    return best
  }

  const sender = createSender({
    // Resolved per send, so a fetch wrapped after mount is still honoured.
    fetchImpl: (url, init) => globalThis.fetch(url, init),
    url: `${apiBaseUrl}/page-performance/track`,
  })
  const batcher = createBatcher({
    send: sender,
    now,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h),
  })

  const tracker = createOpenTracker({
    now,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h),
    // "The next frame was painted": a task queued from requestAnimationFrame
    // runs just after that frame's paint.
    frame: (fn) => {
      const token = { cancelled: false, raf: null, to: null }
      const run = () => {
        token.to = setTimeout(() => {
          if (!token.cancelled) fn()
        }, 0)
      }
      if (typeof requestAnimationFrame === 'function') token.raf = requestAnimationFrame(run)
      else token.to = setTimeout(() => !token.cancelled && fn(), 16)
      return token
    },
    cancelFrame: (token) => {
      if (!token) return
      token.cancelled = true
      if (token.raf != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(token.raf)
      if (token.to != null) clearTimeout(token.to)
    },
    newId: uuid,
    onLoad: (record) => {
      if (mayQueue()) batcher.addLoad(record)
    },
    onEnd: (record) => {
      if (mayQueue()) batcher.addEnd(record)
    },
    getFullLoadMetrics: (endAt) => {
      const out = {}
      const nav = navEntry || perf?.getEntriesByType?.('navigation')?.[0]
      if (nav && nav.requestStart > 0 && nav.responseStart >= nav.requestStart) {
        out.ttfbMs = nav.responseStart - nav.requestStart
      }
      if (typeof fcpMs === 'number') out.fcpMs = fcpMs
      let lcp
      for (const t of lcpTimes) if (t <= endAt) lcp = t
      if (typeof lcp === 'number') out.lcpMs = lcp
      return out
    },
    resolveBytes: (ref) => {
      const e = findResource(ref)
      // Without Timing-Allow-Origin both sizes read 0 — unknown, not empty.
      if (!e || !(e.transferSize > 0 || e.encodedBodySize > 0)) return undefined
      return e.encodedBodySize
    },
    resolveTtfb: (ref) => {
      const e = findResource(ref)
      // Cross-origin without Timing-Allow-Origin these read 0 — unknown.
      if (!e || !(e.requestStart > 0) || !(e.responseStart >= e.requestStart)) return undefined
      return e.responseStart - e.requestStart
    },
    // CLS / INP are null in the end record when their observer is not
    // running (e.g. Safari has no `layout-shift`) — null is "unknown".
    clsSupported: () => clsMeasured,
    inpSupported: () => inpMeasured,
  })

  // ── Performance observers (each type guarded) ─────────────────────
  // Returns true when the observer is running.
  const observe = (type, onEntry, extra = {}) => {
    if (!supports(type)) return false
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          try {
            onEntry(entry)
          } catch {
            /* never throw from the tracker */
          }
        }
      })
      po.observe({ type, buffered: true, ...extra })
      cleanups.push(() => po.disconnect())
      return true
    } catch {
      /* unsupported option — skip this figure */
      return false
    }
  }
  observe('navigation', (e) => {
    navEntry = e
  })
  observe('paint', (e) => {
    if (e.name === 'first-contentful-paint') fcpMs = e.startTime
  })
  observe('largest-contentful-paint', (e) => {
    if (lcpTimes.length < 100) lcpTimes.push(e.startTime)
  })
  clsMeasured = observe('layout-shift', (e) => {
    if (!e.hadRecentInput) tracker.layoutShift(e.value, e.startTime)
  })
  inpMeasured = observe(
    'event',
    (e) => {
      if (e.interactionId > 0) tracker.interaction(e.duration, e.startTime)
    },
    { durationThreshold: 16 }, // the minimum the browser allows
  )
  // Any interaction at all — tells "all fast" (INP 0: no `event` entry
  // reached the 16 ms floor) from "none" (null).
  observe('first-input', (e) => tracker.interactionSeen(e.startTime))
  const onInteraction = (e) => tracker.interactionSeen(e?.timeStamp)
  try {
    const opts = { capture: true, passive: true }
    globalThis.addEventListener?.('pointerdown', onInteraction, opts)
    globalThis.addEventListener?.('keydown', onInteraction, opts)
    cleanups.push(() => {
      globalThis.removeEventListener?.('pointerdown', onInteraction, opts)
      globalThis.removeEventListener?.('keydown', onInteraction, opts)
    })
  } catch {
    /* no window — INP stays unknown for interactions below the floor */
  }
  if (apiOrigin) {
    observe('resource', (e) => {
      if (typeof e.name !== 'string' || !e.name.startsWith(apiOrigin)) return
      const list = resources.get(e.name) || []
      list.push(e)
      resources.set(e.name, list)
      resourceCount += 1
      if (resourceCount > MAX_RESOURCE_ENTRIES) {
        const oldest = resources.keys().next().value
        resourceCount -= resources.get(oldest)?.length || 0
        resources.delete(oldest)
      }
    })
  }

  // ── Page hide: the open in progress is abandoned — reported up to this
  //    moment, so it never includes background time; send what is queued.
  const onHidden = () => {
    try {
      if (globalThis.document?.visibilityState === 'hidden') {
        tracker.documentHidden(now())
        batcher.flushUrgent()
      }
    } catch {
      /* never throw from the tracker */
    }
  }
  const onPageHide = () => {
    try {
      tracker.documentHidden(now())
      batcher.flushUrgent()
    } catch {
      /* never throw from the tracker */
    }
  }
  try {
    globalThis.document?.addEventListener('visibilitychange', onHidden)
    globalThis.addEventListener?.('pagehide', onPageHide)
    cleanups.push(() => {
      globalThis.document?.removeEventListener('visibilitychange', onHidden)
      globalThis.removeEventListener?.('pagehide', onPageHide)
    })
  } catch {
    /* no document — nothing to listen to */
  }

  // ── The one interceptor pair on `api` ─────────────────────────────
  // (call ids come from the module-level `callSeq`, so they never collide
  // across engines when a gate remounts the tracker mid-call)
  const endCall = (config, status, bytes, cancelled) => {
    try {
      const id = config?.[ID_FIELD]
      if (id != null) tracker.requestEnded(id, { status, bytes, at: now(), cancelled })
    } catch {
      /* never throw from the tracker */
    }
  }
  try {
    const previous = api[PAIR_KEY]
    if (previous) {
      api.interceptors.request.eject(previous.request)
      api.interceptors.response.eject(previous.response)
    }
    const request = api.interceptors.request.use((config) => {
      try {
        if (config?.[ID_FIELD] != null && tracker.isPending(config[ID_FIELD])) return config
        const uri = api.getUri(config)
        const path = normalizeApiPath(uri, apiBaseUrl)
        if (IGNORED_PATHS.some((p) => path === p || path.endsWith(p))) return config
        const at = now()
        let url = uri
        try {
          url = new URL(uri, globalThis.location?.href).href
        } catch {
          /* keep the relative form */
        }
        callSeq += 1
        const id = `c${callSeq}`
        const accepted = tracker.requestStarted(id, {
          method: config.method,
          path,
          at,
          ref: { url, at },
        })
        config[ID_FIELD] = accepted ? id : null
      } catch {
        /* never throw from the tracker */
      }
      return config
    })
    const response = api.interceptors.response.use(
      (res) => {
        endCall(res?.config, res?.status ?? 0, contentLength(res?.headers), false)
        return res
      },
      (error) => {
        endCall(
          error?.config,
          error?.response?.status ?? 0,
          contentLength(error?.response?.headers),
          axios.isCancel(error),
        )
        return Promise.reject(error)
      },
    )
    const pair = { request, response }
    api[PAIR_KEY] = pair
    cleanups.push(() => {
      api.interceptors.request.eject(request)
      api.interceptors.response.eject(response)
      if (api[PAIR_KEY] === pair) delete api[PAIR_KEY]
    })
  } catch {
    /* no interceptors — loads are still timed, without their calls */
  }

  /** Was the document hidden at any time before now (e.g. opened in a
   *  background tab)? Uses the visibility-state timeline where supported. */
  const wasHiddenEarlier = () => {
    try {
      if (!supports('visibility-state')) return false
      return (perf.getEntriesByType('visibility-state') || []).some((e) => e.name === 'hidden')
    } catch {
      return false
    }
  }

  return {
    start(pathname) {
      try {
        suspended = false
        const claimFull = !started && !globalThis[FULL_KEY]
        started = true
        if (claimFull) {
          globalThis[FULL_KEY] = true
          ownsFullClaim = true
          tracker.start({ route: pathname, kind: 'full', at: 0 })
          // Opened in a background tab: the timings are meaningless.
          if (wasHiddenEarlier()) tracker.dropOpen()
        } else {
          tracker.start({ route: pathname, kind: 'spa', at: now() })
        }
        if (globalThis.document?.visibilityState === 'hidden') tracker.dropOpen()
      } catch {
        /* never throw from the tracker */
      }
    },
    /** The gate is closing (it still holds right now): report the open in
     *  progress as abandoned and send everything queued. */
    gateClosing() {
      try {
        tracker.abandonOpen(now())
        batcher.flushUrgent()
      } catch {
        /* never throw from the tracker */
      }
    },
    /** Before an account switch: report + send now, then queue nothing
     *  until the next route change. */
    flushNow() {
      try {
        tracker.abandonOpen(now())
        // Hands a body to the transport NOW, even when an earlier one is
        // still on the wire (within the keepalive budget).
        batcher.flushUrgent()
        // Only bodies handed to the transport just now are sure to leave
        // under this account's cookie — the switch POST follows at once. Any
        // further body would go out under the NEW account, so it is dropped
        // rather than misfiled (a rare >48 KB queue loses its tail).
        batcher.clear()
      } finally {
        suspended = true
      }
    },
    /** Stale tab: the cookie belongs to someone else — discard the open in
     *  progress and everything not yet sent. */
    clearAll() {
      suspended = true
      tracker.dropOpen()
      tracker.forgetSession()
      batcher.clear()
    },
    dispose() {
      try {
        // A StrictMode / HMR remount while the full load is still being
        // timed hands the full load to the next engine.
        if (ownsFullClaim && tracker.snapshot().open?.kind === 'full') globalThis[FULL_KEY] = false
        tracker.dispose()
        for (const fn of cleanups.splice(0)) {
          try {
            fn()
          } catch {
            /* keep tearing down */
          }
        }
        if (mayQueue()) batcher.flush()
        else batcher.clear()
      } catch {
        /* never throw from the tracker */
      }
    },
  }
}

export function PagePerfTracker() {
  const { pathname } = useLocation()
  const status = useAuthStore((s) => s.status)
  const role = useAuthStore((s) => s.user?.role)
  const impersonation = useAuthStore((s) => s.impersonation)
  const enabled = shouldTrackVisits({ status, role, impersonation })

  const enabledRef = useRef(enabled)
  const engineRef = useRef(null)

  useLayoutEffect(() => {
    // The gate is closing (signed out, impersonation began): report the open
    // in progress as abandoned while the gate still holds, then close it.
    if (enabledRef.current && !enabled) engineRef.current?.gateClosing()
    enabledRef.current = enabled
  }, [enabled])

  // Set-up first (declared first, so it runs before the route effect).
  useLayoutEffect(() => {
    let engine = null
    try {
      engine = createEngine(() => enabledRef.current)
    } catch {
      engine = null
    }
    engineRef.current = engine
    const unregister = registerPagePerf(engine)
    return () => {
      unregister()
      engineRef.current = null
      engine?.dispose()
    }
  }, [])

  // Layout effect so the open starts before the new page's own effects
  // fire their API calls.
  useLayoutEffect(() => {
    engineRef.current?.start(pathname)
  }, [pathname])

  return null
}

export default PagePerfTracker
